/**
 * @module services/catalogs/swisscoveryService
 * @description Service for querying Swisscovery (Swiss Library Service Platform - SLSP) via SRU protocol
 *
 * Swisscovery is the shared catalog of Swiss academic libraries managed by SLSP.
 * This service uses the SRU (Search/Retrieve via URL) protocol version 1.2
 * to search and retrieve bibliographic records in MARCXML format.
 *
 * @see https://swisscovery.slsp.ch
 * @see https://slsp.ch/en/metadata
 */

import type { ItemData, ItemType, Creator, CreatorType } from '@types';
import type { BaseCatalogRecord, SRUSearchOptions } from './types';
import { CATALOG_PROXY_PATHS } from './types';
import { SRUBaseService } from './sruBase';
import {
  mapLanguageCode,
  extractPageCount,
  cleanSwisscoveryName,
  cleanSwisscoveryTitle,
  capitalizePublisher,
} from './utils';
import { getCreatorTypesForItemType, getDefaultCreatorType } from '@/data/itemTypes';

// =============================================================================
// TYPES
// =============================================================================

/** Swisscovery search result */
export interface SwisscoveryRecord extends BaseCatalogRecord {
  /** MMS ID from Alma */
  mmsId: string;
}

/** Swisscovery search response */
export interface SwisscoverySearchResponse {
  totalResults: number;
  records: SwisscoveryRecord[];
}

/** Search index options for Swisscovery (Alma indexes) */
export type SwisscoverySearchIndex =
  | 'alma.all_for_ui' // All fields
  | 'alma.title' // Title
  | 'alma.author' // Author (preferred over alma.creator)
  | 'alma.creator' // Creator (may not be configured)
  | 'alma.subject' // Subject
  | 'alma.isbn' // ISBN
  | 'alma.issn' // ISSN
  | 'alma.date' // Publication date
  | 'alma.publisher'; // Publisher

/** Swisscovery search options */
export interface SwisscoverySearchOptions extends SRUSearchOptions {
  index?: SwisscoverySearchIndex;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const SWISSCOVERY_CATALOG_URL = 'https://swisscovery.slsp.ch';

// =============================================================================
// SWISSCOVERY SERVICE (MARCXML)
// =============================================================================

class SwisscoveryService extends SRUBaseService<SwisscoveryRecord, SwisscoverySearchOptions> {
  protected readonly serviceName = 'Swisscovery';
  protected readonly proxyPath = CATALOG_PROXY_PATHS.swisscovery;
  protected readonly defaultRecordSchema = 'marcxml';

  /**
   * Search in the Swisscovery catalog
   */
  async search(
    query: string,
    options: SwisscoverySearchOptions = {}
  ): Promise<SwisscoverySearchResponse> {
    const { index = 'alma.all_for_ui', startRecord = 1, maximumRecords = 10 } = options;

    // Build CQL query
    // Use 'all' operator for text searches (searches for words, more flexible)
    // Use '=' for exact matches like ISBN/ISSN
    const operator = index === 'alma.isbn' || index === 'alma.issn' ? '=' : 'all';
    const cqlQuery = `${index} ${operator} "${this.escapeCQL(query)}"`;

    return this.executeSearch(cqlQuery, {
      startRecord,
      maximumRecords,
    }) as Promise<SwisscoverySearchResponse>;
  }

  /**
   * Search by ISBN
   */
  async searchByISBN(isbn: string): Promise<SwisscoveryRecord | null> {
    const cleanIsbn = isbn.replace(/[-\s]/g, '');
    const result = await this.search(cleanIsbn, { index: 'alma.isbn', maximumRecords: 1 });
    return result.records[0] || null;
  }

  /**
   * Parse recordData element containing MARCXML record
   */
  protected parseRecordData(recordDataEl: Element): SwisscoveryRecord | null {
    // Find the record element (MARC record)
    const recordEl = this.findElementByLocalName(recordDataEl, 'record');
    if (!recordEl) {
      return null;
    }

    // Get MMS ID from controlfield 001
    const mmsId = this.getControlField(recordEl, '001') || '';

    return this.parseMarcRecord(recordEl, mmsId);
  }

  /**
   * Parse a MARC21 record to Zotero format
   */
  private parseMarcRecord(recordEl: Element, mmsId: string): SwisscoveryRecord | null {
    const item: Partial<ItemData> = {
      creators: [],
    };

    // Determine item type from leader
    const leader = this.getLeader(recordEl);
    item.itemType = this.mapLeaderToItemType(leader);

    // Get title from 245
    const title = this.getTitle(recordEl);
    if (title) {
      item.title = cleanSwisscoveryTitle(title);
    }

    // Get creators from 100, 700, 110, 710
    item.creators = this.getCreators(recordEl, item.itemType);

    // Get publication info from 260 or 264
    const pubInfo = this.getPublicationInfo(recordEl);
    if (pubInfo.place) item.place = capitalizePublisher(pubInfo.place);
    if (pubInfo.publisher) item.publisher = capitalizePublisher(pubInfo.publisher);
    if (pubInfo.date) item.date = pubInfo.date;

    // Get ISBN from 020
    const isbn = this.getDataFieldSubfield(recordEl, '020', 'a');
    if (isbn) {
      item.ISBN = isbn.replace(/[^0-9X]/gi, '');
    }

    // Get ISSN from 022
    const issn = this.getDataFieldSubfield(recordEl, '022', 'a');
    if (issn) {
      item.ISSN = issn;
    }

    // Get physical description from 300
    const extent = this.getDataFieldSubfield(recordEl, '300', 'a');
    if (extent) {
      const pageCount = extractPageCount(extent);
      if (pageCount) {
        item.numPages = pageCount;
      }
    }

    // Get language from 041 or 008
    const language =
      this.getDataFieldSubfield(recordEl, '041', 'a') || this.getLanguageFrom008(recordEl);
    if (language) {
      item.language = mapLanguageCode(language);
    }

    // Get abstract from 520
    const abstract = this.getDataFieldSubfield(recordEl, '520', 'a');
    if (abstract) {
      item.abstractNote = abstract;
    }

    // Get edition from 250
    const edition = this.getDataFieldSubfield(recordEl, '250', 'a');
    if (edition) {
      item.edition = edition;
    }

    // Get series from 490
    const series = this.getDataFieldSubfield(recordEl, '490', 'a');
    if (series) {
      item.series = cleanSwisscoveryTitle(series);
    }

    // Validate
    this.validateItem(item);

    // Build catalog URL
    let catalogUrl: string | undefined;
    if (mmsId) {
      catalogUrl = `${SWISSCOVERY_CATALOG_URL}/discovery/fulldisplay?docid=alma${mmsId}&context=L&vid=41SLSP_NETWORK:VU1_UNION`;
    }

    return {
      id: mmsId || `swisscovery-${Date.now()}`,
      mmsId: mmsId || `swisscovery-${Date.now()}`,
      item,
      catalogUrl,
    };
  }

  // ==========================================================================
  // MARC Parsing Helpers
  // ==========================================================================

  /**
   * Get leader from MARC record
   */
  private getLeader(recordEl: Element): string {
    const leaderEl = this.findElementByLocalName(recordEl, 'leader');
    return leaderEl?.textContent || '';
  }

  /**
   * Get control field value by tag
   */
  private getControlField(recordEl: Element, tag: string): string | null {
    const controlFields = this.findAllElementsByLocalName(recordEl, 'controlfield');
    for (const cf of controlFields) {
      if (cf.getAttribute('tag') === tag) {
        return cf.textContent?.trim() || null;
      }
    }
    return null;
  }

  /**
   * Get data field subfield value
   */
  private getDataFieldSubfield(
    recordEl: Element,
    tag: string,
    subfieldCode: string
  ): string | null {
    const dataFields = this.findAllElementsByLocalName(recordEl, 'datafield');
    for (const df of dataFields) {
      if (df.getAttribute('tag') === tag) {
        const subfields = this.findAllElementsByLocalName(df, 'subfield');
        for (const sf of subfields) {
          if (sf.getAttribute('code') === subfieldCode) {
            return sf.textContent?.trim() || null;
          }
        }
      }
    }
    return null;
  }

  /**
   * Get all subfields from a data field
   */
  private getDataFieldSubfields(recordEl: Element, tag: string): Map<string, string[]> {
    const result = new Map<string, string[]>();
    const dataFields = this.findAllElementsByLocalName(recordEl, 'datafield');

    for (const df of dataFields) {
      if (df.getAttribute('tag') === tag) {
        const subfields = this.findAllElementsByLocalName(df, 'subfield');
        for (const sf of subfields) {
          const code = sf.getAttribute('code');
          const value = sf.textContent?.trim();
          if (code && value) {
            if (!result.has(code)) {
              result.set(code, []);
            }
            result.get(code)!.push(value);
          }
        }
        break; // Only get first matching datafield
      }
    }
    return result;
  }

  /**
   * Get all data fields with a specific tag
   */
  private getAllDataFields(recordEl: Element, tag: string): Element[] {
    const dataFields = this.findAllElementsByLocalName(recordEl, 'datafield');
    return dataFields.filter((df) => df.getAttribute('tag') === tag);
  }

  /**
   * Get title from 245 field
   */
  private getTitle(recordEl: Element): string | null {
    const subfields = this.getDataFieldSubfields(recordEl, '245');
    const titleParts: string[] = [];

    // $a - Title
    const titleA = subfields.get('a')?.[0];
    if (titleA) {
      titleParts.push(titleA.replace(/[\s/:;,]+$/, ''));
    }

    // $b - Subtitle
    const titleB = subfields.get('b')?.[0];
    if (titleB) {
      titleParts.push(titleB.replace(/[\s/:;,]+$/, ''));
    }

    // $n - Number of part/section
    const titleN = subfields.get('n')?.[0];
    if (titleN) {
      titleParts.push(titleN.replace(/[\s/:;,]+$/, ''));
    }

    // $p - Name of part/section
    const titleP = subfields.get('p')?.[0];
    if (titleP) {
      titleParts.push(titleP.replace(/[\s/:;,]+$/, ''));
    }

    return titleParts.length > 0 ? titleParts.join(' : ') : null;
  }

  /**
   * Get creators from 100, 700, 110, 710 fields
   */
  private getCreators(recordEl: Element, itemType: ItemType): Creator[] {
    const creators: Creator[] = [];
    const validTypes = getCreatorTypesForItemType(itemType);
    const defaultType = getDefaultCreatorType(itemType);

    // 100 - Main personal name (author)
    const main100 = this.getAllDataFields(recordEl, '100');
    for (const df of main100) {
      const creator = this.parseCreatorField(df, 'author', validTypes, defaultType);
      if (creator) creators.push(creator);
    }

    // 110 - Main corporate name
    const main110 = this.getAllDataFields(recordEl, '110');
    for (const df of main110) {
      const creator = this.parseCorporateField(df, 'author', validTypes, defaultType);
      if (creator) creators.push(creator);
    }

    // 700 - Added personal name (contributor)
    const added700 = this.getAllDataFields(recordEl, '700');
    for (const df of added700) {
      const creator = this.parseCreatorField(df, 'contributor', validTypes, defaultType);
      if (creator) creators.push(creator);
    }

    // 710 - Added corporate name
    const added710 = this.getAllDataFields(recordEl, '710');
    for (const df of added710) {
      const creator = this.parseCorporateField(df, 'contributor', validTypes, defaultType);
      if (creator) creators.push(creator);
    }

    return creators;
  }

  /**
   * Parse a personal name field (100, 700)
   */
  private parseCreatorField(
    df: Element,
    defaultType: CreatorType,
    validTypes: readonly CreatorType[],
    fallbackType: CreatorType
  ): Creator | null {
    const subfields = this.findAllElementsByLocalName(df, 'subfield');
    let name = '';
    let firstName = '';
    let lastName = '';
    let role = defaultType;

    for (const sf of subfields) {
      const code = sf.getAttribute('code');
      const value = sf.textContent?.trim() || '';

      if (code === 'a') {
        // Name in "LastName, FirstName" format
        const cleanedName = cleanSwisscoveryName(value);
        if (cleanedName.includes(',')) {
          const parts = cleanedName.split(',', 2);
          lastName = parts[0].trim();
          firstName = parts[1]?.trim() || '';
        } else {
          name = cleanedName;
        }
      } else if (code === 'e' || code === '4') {
        // Relator term or code
        role = this.mapRelatorToCreatorType(value, defaultType);
      }
    }

    if (!lastName && !name) return null;

    const creatorType = validTypes.includes(role) ? role : fallbackType;

    if (lastName) {
      return { creatorType, lastName, firstName: firstName || undefined };
    }
    return { creatorType, name };
  }

  /**
   * Parse a corporate name field (110, 710)
   */
  private parseCorporateField(
    df: Element,
    defaultType: CreatorType,
    validTypes: readonly CreatorType[],
    fallbackType: CreatorType
  ): Creator | null {
    const nameEl = this.findAllElementsByLocalName(df, 'subfield').find(
      (sf) => sf.getAttribute('code') === 'a'
    );

    if (!nameEl || !nameEl.textContent?.trim()) return null;

    const name = cleanSwisscoveryName(nameEl.textContent.trim());
    const creatorType = validTypes.includes(defaultType) ? defaultType : fallbackType;

    return { creatorType, name };
  }

  /**
   * Map MARC relator term/code to Zotero creator type
   */
  private mapRelatorToCreatorType(relator: string, defaultType: CreatorType): CreatorType {
    const r = relator.toLowerCase();

    if (r === 'aut' || r.includes('author')) return 'author';
    if (r === 'edt' || r.includes('editor')) return 'editor';
    if (r === 'trl' || r.includes('translator')) return 'translator';
    if (r === 'ctb' || r.includes('contributor')) return 'contributor';
    if (r === 'ill' || r.includes('illustrator')) return 'contributor';
    if (r === 'drt' || r.includes('director')) return 'director';
    if (r === 'cmp' || r.includes('composer')) return 'composer';
    if (r === 'prf' || r.includes('performer')) return 'performer';

    return defaultType;
  }

  /**
   * Get publication info from 260 or 264 field
   */
  private getPublicationInfo(recordEl: Element): {
    place?: string;
    publisher?: string;
    date?: string;
  } {
    const result: { place?: string; publisher?: string; date?: string } = {};

    // Try 264 first (RDA), then 260 (AACR2)
    let subfields = this.getDataFieldSubfields(recordEl, '264');
    if (subfields.size === 0) {
      subfields = this.getDataFieldSubfields(recordEl, '260');
    }

    // $a - Place
    const place = subfields.get('a')?.[0];
    if (place) {
      result.place = place.replace(/[\s:;,[\]]+$/, '').trim();
    }

    // $b - Publisher
    const publisher = subfields.get('b')?.[0];
    if (publisher) {
      result.publisher = publisher.replace(/[\s:;,[\]]+$/, '').trim();
    }

    // $c - Date
    const date = subfields.get('c')?.[0];
    if (date) {
      // Extract 4-digit year
      const yearMatch = date.match(/\d{4}/);
      result.date = yearMatch ? yearMatch[0] : date.replace(/[\s.[\]]+$/, '').trim();
    }

    return result;
  }

  /**
   * Get language code from 008 control field (positions 35-37)
   */
  private getLanguageFrom008(recordEl: Element): string | null {
    const field008 = this.getControlField(recordEl, '008');
    if (field008 && field008.length >= 38) {
      return field008.substring(35, 38);
    }
    return null;
  }

  /**
   * Map MARC leader to Zotero item type
   */
  private mapLeaderToItemType(leader: string): ItemType {
    if (leader.length < 8) return 'book';

    const recordType = leader.charAt(6);
    const bibLevel = leader.charAt(7);

    // Type of record (position 6)
    switch (recordType) {
      case 'a': // Language material
      case 't': // Manuscript language material
        if (bibLevel === 's') return 'journalArticle'; // Serial
        if (bibLevel === 'm') return 'book'; // Monograph
        if (bibLevel === 'a') return 'bookSection'; // Component part
        return 'book';
      case 'c': // Notated music
      case 'd': // Manuscript notated music
        return 'book';
      case 'e': // Cartographic material
      case 'f': // Manuscript cartographic material
        return 'map';
      case 'g': // Projected medium (video, film)
        return 'videoRecording';
      case 'i': // Nonmusical sound recording
      case 'j': // Musical sound recording
        return 'audioRecording';
      case 'k': // Two-dimensional nonprojectable graphic
        return 'artwork';
      case 'm': // Computer file
        return 'computerProgram';
      case 'o': // Kit
      case 'p': // Mixed materials
        return 'document';
      case 'r': // Three-dimensional artifact
        return 'artwork';
      default:
        return 'book';
    }
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const swisscoveryService = new SwisscoveryService();
export default swisscoveryService;
